import { useReducedMotion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Shimmer } from '@/components/ui/shimmer';

export const AGENT_STATUS_SHIMMER_DURATION = 2.2;
export const AGENT_STATUS_SHIMMER_SPREAD = 2;

export interface AgentStatusShimmerRowProps {
  icon: LucideIcon;
  children: string;
  streaming: boolean;
  className?: string;
  duration?: number;
  spread?: number;
}

/**
 * Static icon + status text row. Text shimmers L→R while streaming; icons never animate.
 * Reusable for future memory/tool adoption.
 */
export function AgentStatusShimmerRow({
  icon: Icon,
  children,
  streaming,
  className,
  duration = AGENT_STATUS_SHIMMER_DURATION,
  spread = AGENT_STATUS_SHIMMER_SPREAD,
}: AgentStatusShimmerRowProps) {
  const reduceMotion = useReducedMotion();
  const showShimmer = streaming && !reduceMotion;

  return (
    <div className={cn('flex items-start gap-2', className)}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/75" aria-hidden />
      {showShimmer ? (
        <Shimmer
          as="span"
          duration={duration}
          spread={spread}
          className="flex-1 min-w-0 text-[11px] leading-relaxed"
        >
          {children}
        </Shimmer>
      ) : (
        <p className="flex-1 min-w-0 text-[11px] leading-relaxed text-muted-foreground/75">
          {children}
        </p>
      )}
    </div>
  );
}
